import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';

import path from 'path';

import { DeleteOutlined as DeleteOutlinedIcon, Edit as EditIcon } from '@mui/icons-material';
import { Box, Button, Link as MuiLink, Portal, Stack } from '@mui/material';
import {
	GridColDef,
	GridFilterModel,
	GridInitialState,
	GridPaginationModel,
	GridSlotProps,
	GridSlots,
	PropsFromSlot,
} from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { Link as ReactRouterLink } from 'react-router-dom';
import useSWR from 'swr';

import { DataGrid, DataGridToolbar, GridActionsCellItem } from '~components';
import { pagePathSegments } from '~constants';
import { BaseReference } from '~interfaces';
import { MutationRefProps } from '~interfaces/refProps';

import SkcAccessRule from '../../interfaces/accessRule';
import SkcAccessRulesService from '../../services/skcAccessRulesService';

const toolbarIdString = 'topology-access-rules-datagrid';

const service = new SkcAccessRulesService();

interface AccessRulesDataGridProps {
	enableCreate?: boolean;
	enableEdit?: boolean;
	enableDelete?: boolean;
	onEdit?: (value: BaseReference) => void;
	onDelete?: (value: BaseReference) => void;
	onCreate?: () => void;
}

const AccessRulesDataGrid = forwardRef<MutationRefProps, AccessRulesDataGridProps>(
	({ onEdit, onDelete, onCreate }, ref) => {
		const { t } = useTranslation();

		const columns = useMemo<GridColDef<SkcAccessRule>[]>(
			() => [
				{
					field: 'id',
					headerName: 'ID',
					renderCell: (params) => (
						<MuiLink component={ReactRouterLink} to={params.value.toString()}>
							{params.value}
						</MuiLink>
					),
					width: 80,
					hideable: false,
				},
				{
					field: 'label',
					headerName: t('ui.label.name'),
					flex: 1,
				},
				{
					field: 'description',
					headerName: t('ui.label.description'),
					valueFormatter: (value: string) => value ?? '-',
					flex: 1,
				},
				{
					field: 'startDate',
					headerName: t('ui.label.startDate'),
					valueGetter: (_, row) => row.period.start,
					valueFormatter: (value: Date | null) => (value ? value.toLocaleDateString() : '-'),
					flex: 1,
					type: 'date',
				},
				{
					field: 'endDate',
					headerName: t('ui.label.endDate'),
					valueGetter: (_, row) => row.period.end,
					valueFormatter: (value: Date | undefined) => (value ? value.toLocaleDateString() : '-'),
					flex: 1,
					type: 'date',
				},
				{
					field: 'organisation',
					headerName: t('organisation'),
					// valueFormatter: (value: BaseReference) => value?.label ?? '-',
					renderCell: (params) =>
						params.value != null ?
							<MuiLink
								component={ReactRouterLink}
								to={`/${path.join(pagePathSegments.Organisations, params.value.id.toString(), pagePathSegments.Summary)}`}
							>
								{params.value.label}
							</MuiLink>
						:	'-',
					flex: 1,
				},
				{
					field: 'userGroup',
					headerName: t('accessGroup'),
					// valueGetter: (value: BaseReference) => value.label,
					renderCell: (params) => (
						<MuiLink
							component={ReactRouterLink}
							to={`/${path.join(pagePathSegments.Access, pagePathSegments.AccessGroups, params.value.id.toString())}`}
						>
							{params.value.label}
						</MuiLink>
					),
					flex: 1,
				},
				{
					field: 'deviceGroup',
					headerName: t('deviceGroup'),
					// valueGetter: (value: BaseReference) => value.label,
					renderCell: (params) => (
						<MuiLink
							component={ReactRouterLink}
							to={`/${path.join(pagePathSegments.Access, pagePathSegments.DeviceGroups, params.value.id.toString())}`}
						>
							{params.value.label}
						</MuiLink>
					),
					flex: 1,
				},
				{
					field: 'schedule',
					headerName: t('schedule'),
					// valueGetter: (value: BaseReference) => value.label,
					renderCell: (params) => (
						<MuiLink
							component={ReactRouterLink}
							to={`/${path.join(pagePathSegments.Access, pagePathSegments.Schedules, params.value.id.toString())}`}
						>
							{params.value.label}
						</MuiLink>
					),
					flex: 1,
				},
				{
					field: 'actions',
					type: 'actions',
					display: 'text',
					width: 50,
					resizable: false,
					align: 'right',
					hideable: false,
					getActions: (params) => [
						<GridActionsCellItem
							key={`${params.id}-edit`}
							label={t('ui.edit')}
							icon={<EditIcon />}
							color='inherit'
							onClick={() => onEdit?.({ id: params.id.toString(), label: params.row.label })}
							showInMenu
						/>,
						<GridActionsCellItem
							key={`${params.id}-delete`}
							label={t('ui.delete')}
							icon={<DeleteOutlinedIcon color='error' />}
							color='error'
							onClick={() => onDelete?.({ id: params.id.toString(), label: params.row.label })}
							showInMenu
						/>,
					],
				},
			],
			[t, onEdit, onDelete]
		);

		const [fetchParameters, setFetchParameters] = useState<object>({});
		const { data, error, isLoading, isValidating, mutate } = useSWR(
			[service.basePath, fetchParameters],
			([_, args]) => {
				if (isLoading) {
					service.abortCurrentRequest('Parameters change');
				}

				return service.getAccessRules(args);
			},
			{
				keepPreviousData: true,
			}
		);

		const [totalCount, setTotalCount] = useState<number>(data?.total || 0);

		useEffect(() => {
			if (data?.total != null) {
				setTotalCount(data.total);
			}
		}, [data]);

		useImperativeHandle(
			ref,
			() => ({
				mutate: mutate,
			}),
			[mutate]
		);

		const handleStateRestore = (state: GridInitialState) => {
			const searchQuery = state.filter?.filterModel?.quickFilterValues?.[0];
			const organisation = state.filter?.filterModel?.items.find(
				(el) => el.field === 'organisation'
			)?.value;

			setFetchParameters((prev) => ({
				...prev,
				page:
					state.pagination?.paginationModel?.page != null ?
						state.pagination.paginationModel.page + 1
					:	undefined,
				pageSize: state.pagination?.paginationModel?.pageSize,
				organisationId: organisation?.id,
				searchQuery: searchQuery,
			}));
		};

		const handlePaginationModelChange = (value: GridPaginationModel) => {
			setFetchParameters((prev) => ({
				...prev,
				page: value.page + 1,
				pageSize: value.pageSize,
			}));
		};

		const handleFilterModelChange = (model: GridFilterModel) => {
			const searchQuery = model.quickFilterValues?.[0];
			const organisation = model?.items.find((el) => el.field === 'organisation')?.value;

			setFetchParameters((prev) => ({
				...prev,
				organisationId: organisation?.id,
				searchQuery: searchQuery,
			}));
		};

		return (
			<Stack
				spacing={2}
				sx={{
					display: 'flex',
					height: 1,
				}}
			>
				<Box
					id={toolbarIdString}
					sx={{
						minHeight: 48,
					}}
				/>
				<DataGrid
					disableColumnSorting
					snapshotKey='accr'
					// autoHeight
					columns={columns}
					error={error}
					loading={isLoading || isValidating}
					rows={data?.results ?? []}
					rowCount={totalCount}
					onPaginationModelChange={handlePaginationModelChange}
					onFilterModelChange={handleFilterModelChange}
					onStateRestore={handleStateRestore}
					slots={{
						toolbar: Toolbar as GridSlots['toolbar'],
					}}
					slotProps={{
						toolbar: {
							onCreate: onCreate,
							onBla: () => null,
						} as GridSlotProps['toolbar'],
						noRowsOverlay: {
							title: t('noResourceFound', { resource: t('rules') }),
						},
						noResultsOverlay: {
							title: t('noResourceFound', { resource: t('rules') }),
						},
					}}
					sx={{ flexGrow: 1, flexBasis: 200 }}
				/>
			</Stack>
		);
	}
);

interface ToolbarProps extends PropsFromSlot<GridSlots['toolbar']> {
	onCreate?: () => void;
}

const Toolbar = (props: ToolbarProps) => {
	const { t } = useTranslation();

	return (
		<Portal container={() => document.getElementById(toolbarIdString)}>
			<DataGridToolbar organisationSyncMode='url'>
				<Button variant='contained' onClick={() => props.onCreate?.()}>
					{t('addResource', { resource: t('rule') })}
				</Button>
			</DataGridToolbar>
		</Portal>
	);
};

export default AccessRulesDataGrid;
